import React, { useState, useEffect } from "react";
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CubeIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  QrCodeIcon,
} from "@heroicons/react/24/outline";
import { request } from "config/request";
import Select from "react-select";
import { ListCommanWay } from "API/commonApi";
import { useQuery } from "@tanstack/react-query";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import * as XLSX from "xlsx";
import { soundFX } from "../soundEffects";
import { BoxStatusBadge, BoxTypeBadge } from "../BoxTracker";

const compactSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "white",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.15)" : "none",
    minHeight: "36px",
    fontSize: "12px",
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 8px" }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "IN_STOCK", label: "In Stock" },
  { value: "ASSIGNED", label: "Assigned" },
  { value: "IN_TRANSIT", label: "In Transit" },
  { value: "IN_TRANSFER", label: "In Transfer (Branch)" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "AWAITING_RETURN", label: "Awaiting Return" },
  { value: "DISPOSED", label: "Disposed" },
  { value: "DAMAGED", label: "Damaged" },
];

const TYPE_FILTER_OPTIONS = [
  { value: "", label: "All Box Types" },
  { value: "SINGLE_USE", label: "Single-Use (Carton)" },
  { value: "MULTI_USE", label: "Multi-Use (Reusable)" },
];

export const BoxExplorerTab: React.FC<{
  onOpenTimeline: (qrCode: string) => void;
  onOpenAwbLookup: (awbNo: string) => void;
  onOpenReplaceModal: (qrCode: string, awbNo?: string) => void;
  onPrintSticker?: (qrCode: string) => void;
}> = ({ onOpenTimeline, onOpenAwbLookup, onOpenReplaceModal, onPrintSticker }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<any>(STATUS_FILTER_OPTIONS[0]);
  const [selectedType, setSelectedType] = useState<any>(TYPE_FILTER_OPTIONS[0]);
  const [selectedSubbranch, setSelectedSubbranch] = useState<any>(null);

  const [boxes, setBoxes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState<number | null>(null);
  const [kpis, setKpis] = useState<{
    total?: number;
    inStock?: number;
    assigned?: number;
    inTransit?: number;
    inTransfer?: number;
    delivered?: number;
    awaitingReturn?: number;
    disposed?: number;
    damaged?: number;
    singleUse?: number;
    multiUse?: number;
    byStatus?: Record<string, number>;
    byBoxType?: Record<string, number>;
  } | null>(null);

  const { data: subbranchesData } = useQuery(["SubBranchesForBoxExplorer"], () =>
    ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 })
  );

  const subbranchOptions = [
    { value: "", label: "All Hubs / Subbranches" },
    ...(subbranchesData?.data?.data || []).map((sb: any) => ({
      value: sb.id,
      label: `${sb.branch_name || sb.name || "Hub"} ${sb.code ? `(${sb.code})` : ""}`,
    })),
  ];

  const fetchStandaloneKpi = async (subbranchId?: any) => {
    try {
      const res: any = await request({
        url: subbranchId ? `/admin/box-tracker/kpi?subbranch=${encodeURIComponent(subbranchId)}` : "/admin/box-tracker/kpi",
        method: "GET",
      });
      if (res?.status === "SUCCESS" && res.data) {
        setKpis(res.data);
      }
    } catch {
      // Non-critical fallback
    }
  };

  const fetchBoxes = async () => {
    setLoading(true);
    try {
      const payload: any = {
        page,
        limit,
        options: { page, limit },
      };
      if (searchQuery.trim()) payload.search = searchQuery.trim();
      if (selectedStatus?.value) payload.status = selectedStatus.value;
      if (selectedType?.value) payload.boxType = selectedType.value;
      if (selectedSubbranch?.value) payload.subbranch = selectedSubbranch.value;

      const res: any = await request({
        url: "/admin/box-tracker/list",
        method: "POST",
        body: payload,
      });

      if (res?.status === "SUCCESS" && res.data) {
        setBoxes(res.data.data || []);
        const total = res.data.total ?? res.data.paginator?.itemCount ?? (res.data.data || []).length;
        setTotalCount(total);
        if (typeof res.data.totalPages === "number") {
          setApiTotalPages(res.data.totalPages);
        } else {
          setApiTotalPages(null);
        }
        if (res.data.kpis) {
          setKpis(res.data.kpis);
        }
      } else {
        setBoxes([]);
        setTotalCount(0);
        setApiTotalPages(null);
      }
    } catch (err: any) {
      UseToast(typeof err === "string" ? err : err?.message || "Failed to load boxes", "error");
      setBoxes([]);
      setTotalCount(0);
      setApiTotalPages(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoxes();
  }, [page, limit, selectedStatus, selectedType, selectedSubbranch]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedStatus(STATUS_FILTER_OPTIONS[0]);
    setSelectedType(TYPE_FILTER_OPTIONS[0]);
    setSelectedSubbranch(subbranchOptions[0]);
    setPage(1);
  };

  const handleMarkDamagedDirect = async (qrCode: string) => {
    const { value: reason } = await Swal.fire({
      title: `Mark Box '${qrCode}' as Damaged`,
      text: "Specify damage condition noticed in warehouse:",
      input: "textarea",
      inputPlaceholder: "e.g. Water damage, crushed carton, torn corner...",
      showCancelButton: true,
      confirmButtonColor: "#e11d48",
      confirmButtonText: "Confirm Damage",
      inputValidator: (value) => {
        if (!value || !value.trim()) return "Please provide a damage reason!";
      },
    });

    if (reason) {
      try {
        const res: any = await request({
          url: "/admin/box-tracker/mark-damaged",
          method: "POST",
          body: { qrCode, reason: reason.trim() },
        });
        if (res?.status === "SUCCESS") {
          soundFX.playSuccess();
          UseToast(`Box '${qrCode}' marked as DAMAGED.`, "success");
          fetchBoxes();
        } else {
          soundFX.playError();
          UseToast(res?.message || "Failed to mark box as damaged.", "error");
        }
      } catch (err: any) {
        soundFX.playError();
        UseToast(typeof err === "string" ? err : err?.message || "Error marking box damaged.", "error");
      }
    }
  };

  const handleExportExcel = () => {
    if (boxes.length === 0) {
      UseToast("No records to export.", "warning");
      return;
    }
    const exportRows = boxes.map((b) => ({
      "QR Code": b.qrCode,
      "Box Type": b.boxType,
      Status: b.status,
      "Current AWB": b.currentAwbNo || "N/A",
      "Total Trips": b.usageCount ?? 0,
      Hub: b._subbranch?.branch_name || "N/A",
      "Inventory Item": b._inventory?.name || "N/A",
      "Damage Reason": b.damageReason || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Box_Stock");
    XLSX.writeFile(workbook, `Airfield_Boxes_${new Date().toISOString().slice(0, 10)}.xlsx`);
    UseToast("Box inventory exported!", "success");
  };

  const totalPages = apiTotalPages || Math.max(1, Math.ceil(totalCount / limit));
  const totalTracked = kpis?.total ?? totalCount;
  const inStockCount = kpis?.inStock ?? boxes.filter((b) => b.status === "IN_STOCK").length;
  const inTransitCount =
    (kpis
      ? (kpis.inTransit || 0) + (kpis.assigned || 0) + (kpis.inTransfer || 0)
      : null) ?? boxes.filter((b) => b.status === "IN_TRANSIT" || b.status === "ASSIGNED").length;
  const awaitingReturnCount = kpis?.awaitingReturn ?? boxes.filter((b) => b.status === "AWAITING_RETURN").length;
  const damagedCount = kpis?.damaged ?? boxes.filter((b) => b.status === "DAMAGED" || b.isDamaged).length;

  return (
    <div className="space-y-6">
      {/* Top Counters */}
      <div className="grid grid-cols-5 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-3.5">
        <div
          onClick={() => {
            setSelectedStatus(STATUS_FILTER_OPTIONS[0]);
            setPage(1);
          }}
          className={`p-4 bg-white rounded-2xl border shadow-xs flex items-center gap-3 cursor-pointer transition-all hover:shadow-sm ${
            !selectedStatus?.value ? "border-[#0077B6] ring-1 ring-[#0077B6]/30 bg-sky-50/20" : "border-slate-200"
          }`}
          title="Filter: All Statuses"
        >
          <div className="p-2.5 bg-sky-50 rounded-xl text-[#0077B6]">
            <CubeIcon className="w-5 h-5" />
          </div>
          <div>
            <span className="block text-slate-400 text-[11px] font-semibold uppercase">Total Tracked</span>
            <span className="text-lg font-bold text-slate-900 font-mono">{totalTracked}</span>
            {kpis?.singleUse !== undefined && kpis?.multiUse !== undefined && (
              <span className="block text-[10px] text-slate-500 font-medium">
                {kpis.singleUse} single &bull; {kpis.multiUse} multi
              </span>
            )}
          </div>
        </div>

        <div
          onClick={() => {
            const opt = STATUS_FILTER_OPTIONS.find((o) => o.value === "IN_STOCK") || STATUS_FILTER_OPTIONS[0];
            setSelectedStatus(opt);
            setPage(1);
          }}
          className={`p-4 bg-white rounded-2xl border shadow-xs flex items-center gap-3 cursor-pointer transition-all hover:shadow-sm ${
            selectedStatus?.value === "IN_STOCK"
              ? "border-emerald-500 ring-1 ring-emerald-500/30 bg-emerald-50/40"
              : "border-emerald-200 bg-emerald-50/20"
          }`}
          title="Filter by In Stock"
        >
          <div className="p-2.5 bg-emerald-100 rounded-xl text-emerald-700">
            <CheckCircleIcon className="w-5 h-5" />
          </div>
          <div>
            <span className="block text-emerald-800 text-[11px] font-semibold uppercase">In Stock</span>
            <span className="text-lg font-bold text-emerald-900 font-mono">{inStockCount}</span>
          </div>
        </div>

        <div
          onClick={() => {
            const opt = STATUS_FILTER_OPTIONS.find((o) => o.value === "IN_TRANSIT") || STATUS_FILTER_OPTIONS[0];
            setSelectedStatus(opt);
            setPage(1);
          }}
          className={`p-4 bg-white rounded-2xl border shadow-xs flex items-center gap-3 cursor-pointer transition-all hover:shadow-sm ${
            selectedStatus?.value === "IN_TRANSIT"
              ? "border-indigo-500 ring-1 ring-indigo-500/30 bg-indigo-50/40"
              : "border-indigo-200 bg-indigo-50/20"
          }`}
          title="Filter by In Transit"
        >
          <div className="p-2.5 bg-indigo-100 rounded-xl text-indigo-700">
            <TruckIcon className="w-5 h-5" />
          </div>
          <div>
            <span className="block text-indigo-800 text-[11px] font-semibold uppercase">In Transit</span>
            <span className="text-lg font-bold text-indigo-900 font-mono">{inTransitCount}</span>
            {Boolean(kpis?.inTransfer) && (
              <span className="block text-[10px] text-indigo-600 font-medium">
                +{kpis?.inTransfer} in transfer
              </span>
            )}
          </div>
        </div>

        <div
          onClick={() => {
            const opt = STATUS_FILTER_OPTIONS.find((o) => o.value === "AWAITING_RETURN") || STATUS_FILTER_OPTIONS[0];
            setSelectedStatus(opt);
            setPage(1);
          }}
          className={`p-4 bg-white rounded-2xl border shadow-xs flex items-center gap-3 cursor-pointer transition-all hover:shadow-sm ${
            selectedStatus?.value === "AWAITING_RETURN"
              ? "border-amber-500 ring-1 ring-amber-500/30 bg-amber-50/40"
              : "border-amber-200 bg-amber-50/20"
          }`}
          title="Filter by Awaiting Return"
        >
          <div className="p-2.5 bg-amber-100 rounded-xl text-amber-700">
            <ArrowUturnLeftIcon className="w-5 h-5" />
          </div>
          <div>
            <span className="block text-amber-800 text-[11px] font-semibold uppercase">Awaiting Return</span>
            <span className="text-lg font-bold text-amber-900 font-mono">{awaitingReturnCount}</span>
          </div>
        </div>

        <div
          onClick={() => {
            const opt = STATUS_FILTER_OPTIONS.find((o) => o.value === "DAMAGED") || STATUS_FILTER_OPTIONS[0];
            setSelectedStatus(opt);
            setPage(1);
          }}
          className={`p-4 bg-white rounded-2xl border shadow-xs flex items-center gap-3 cursor-pointer transition-all hover:shadow-sm ${
            selectedStatus?.value === "DAMAGED"
              ? "border-rose-500 ring-1 ring-rose-500/30 bg-rose-50/40"
              : "border-rose-200 bg-rose-50/20"
          }`}
          title="Filter by Damaged"
        >
          <div className="p-2.5 bg-rose-100 rounded-xl text-rose-700">
            <ExclamationTriangleIcon className="w-5 h-5" />
          </div>
          <div>
            <span className="block text-rose-800 text-[11px] font-semibold uppercase">Damaged</span>
            <span className="text-lg font-bold text-rose-900 font-mono">{damagedCount}</span>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-4 space-y-3">
        <div className="grid grid-cols-5 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-3">
          <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchBoxes(); }} className="relative col-span-2 md:col-span-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Box QR or AWB Number..."
              className="w-full pl-9 pr-16 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077B6] font-mono"
            />
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <button type="submit" className="absolute right-1.5 top-1.5 px-2.5 py-1 bg-[#0077B6] hover:bg-[#023e8a] text-white text-[11px] font-semibold rounded-md">
              Filter
            </button>
          </form>

          <Select options={STATUS_FILTER_OPTIONS} value={selectedStatus} onChange={setSelectedStatus} styles={compactSelectStyles} placeholder="Status..." />
          <Select options={TYPE_FILTER_OPTIONS} value={selectedType} onChange={setSelectedType} styles={compactSelectStyles} placeholder="Box Type..." />
          <Select options={subbranchOptions} value={selectedSubbranch} onChange={setSelectedSubbranch} styles={compactSelectStyles} placeholder="Hub..." />
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <span>Showing <span className="font-bold text-slate-800">{boxes.length}</span> of <span className="font-bold text-slate-800">{totalCount}</span></span>
            <button type="button" onClick={handleResetFilters} className="text-[#0077B6] hover:underline text-[11px] font-semibold">
              Reset Filters
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={handleExportExcel} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg text-xs flex items-center gap-1.5">
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
              <span>Export Excel</span>
            </button>
            <button type="button" onClick={() => fetchBoxes()} className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs" title="Refresh">
              <ArrowPathIcon className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-3.5 px-4">Box QR Code</th>
                <th className="py-3.5 px-4">Type</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4">Assigned AWB</th>
                <th className="py-3.5 px-4">Hub / Subbranch</th>
                <th className="py-3.5 px-4">Trips</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-slate-400">Loading box inventory units...</td>
                </tr>
              ) : boxes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-slate-400">No box units found.</td>
                </tr>
              ) : (
                boxes.map((box: any) => (
                  <tr key={box.id || box.qrCode} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4">
                      <button type="button" onClick={() => onOpenTimeline(box.qrCode)} className="font-mono font-bold text-[#0077B6] hover:underline flex items-center gap-1.5">
                        <QrCodeIcon className="w-3.5 h-3.5 text-slate-400" />
                        <span>{box.qrCode}</span>
                      </button>
                    </td>

                    <td className="py-3 px-4">
                      <BoxTypeBadge type={box.boxType} usageCount={box.usageCount} size="sm" />
                    </td>

                    <td className="py-3 px-4">
                      <BoxStatusBadge status={box.status} size="sm" />
                    </td>

                    <td className="py-3 px-4 font-mono font-bold">
                      {box.currentAwbNo ? (
                        <button type="button" onClick={() => onOpenAwbLookup(box.currentAwbNo)} className="text-slate-800 hover:text-[#0077B6] hover:underline">
                          {box.currentAwbNo}
                        </button>
                      ) : (
                        <span className="text-slate-300 font-normal">—</span>
                      )}
                    </td>

                    <td className="py-3 px-4 text-slate-600 truncate max-w-[150px]">
                      {box._subbranch?.branch_name || "—"}
                    </td>

                    <td className="py-3 px-4">
                      <span className="font-mono font-bold text-slate-800">{box.usageCount ?? 0}</span> trips
                    </td>

                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => onOpenTimeline(box.qrCode)}
                          className="px-2 py-1 bg-sky-50 hover:bg-sky-100 text-[#0077B6] font-semibold rounded text-[11px]"
                        >
                          Journey
                        </button>
                        {box.status !== "DAMAGED" && box.status !== "DISPOSED" && (
                          <button
                            type="button"
                            onClick={() => onOpenReplaceModal(box.qrCode, box.currentAwbNo || undefined)}
                            className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 font-semibold rounded text-[11px]"
                          >
                            Replace
                          </button>
                        )}
                        {box.status !== "DAMAGED" && !box.currentAwbNo && (
                          <button
                            type="button"
                            onClick={() => handleMarkDamagedDirect(box.qrCode)}
                            className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 font-semibold rounded text-[11px]"
                          >
                            Damage
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between flex-wrap gap-2 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <span>Rows:</span>
            <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1 bg-white">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span>Page <span className="font-bold">{page}</span> of <span className="font-bold">{totalPages}</span></span>
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-1 rounded border bg-white disabled:opacity-40">
              <ChevronLeftIcon className="w-3.5 h-3.5" />
            </button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="p-1 rounded border bg-white disabled:opacity-40">
              <ChevronRightIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
